import React, { useMemo } from 'react';
import { GenericFormPage } from '../../../components/generic';
import { useExpenseSubCategory, ExpenseSubCategory } from './useData';
import { useExpenseCategories } from '../p_crm_expense_category/useData';
import {
  moduleConfig,
  getInitialFormData,
  mapItemToFormData,
} from './config';

const Form: React.FC = () => {
  const { expenseCategories } = useExpenseCategories();

  // Update the exp_category_id field with dynamic options
  const configWithCategoryOptions = useMemo(() => {
    const fields = moduleConfig.fields.map((field) => {
      if (field.name === 'exp_category_id') {
        return {
          ...field,
          type: 'select' as const,
          label: 'Category',
          placeholder: 'Select a category (optional)',
          options: [
            { value: '', label: 'None' },
            ...(expenseCategories || []).map((cat) => ({
              value: cat.id,
              label: cat.name || `Category #${cat.id}`,
            })),
          ],
        };
      }
      return field;
    });

    // Custom mapItemToFormData that converts exp_category_id to string for select
    type FormExpenseSubCategory = Omit<ExpenseSubCategory, 'exp_category_id'> & { exp_category_id?: string | number };
    const customMapItemToFormData = (item: ExpenseSubCategory): Partial<FormExpenseSubCategory> => {
      const baseData = mapItemToFormData(item);
      return {
        ...baseData,
        exp_category_id: baseData.exp_category_id ? String(baseData.exp_category_id) : '',
      };
    };

    // Custom getInitialFormData
    const customGetInitialFormData = (): Partial<FormExpenseSubCategory> => {
      return {
        ...getInitialFormData(),
        exp_category_id: '',
      };
    };

    // Custom create/update handlers that convert exp_category_id string back to number
    const customCreate = async (data: Partial<FormExpenseSubCategory>) => {
      const processedData: Partial<ExpenseSubCategory> = {
        ...data,
        exp_category_id: (data.exp_category_id !== undefined && data.exp_category_id !== null && String(data.exp_category_id) !== '') 
          ? Number(data.exp_category_id) 
          : undefined,
      };
      return await moduleConfig.form.create(processedData);
    };

    const customUpdate = async (id: number, data: Partial<FormExpenseSubCategory>) => {
      const processedData: Partial<ExpenseSubCategory> = {
        ...data,
        exp_category_id: (data.exp_category_id !== undefined && data.exp_category_id !== null && String(data.exp_category_id) !== '') 
          ? Number(data.exp_category_id) 
          : undefined,
      };
      return await moduleConfig.form.update(id, processedData);
    };

    return {
      ...moduleConfig,
      fields,
      getInitialFormData: customGetInitialFormData as () => Partial<ExpenseSubCategory>,
      mapItemToFormData: customMapItemToFormData as (item: ExpenseSubCategory) => Partial<ExpenseSubCategory>,
      form: {
        ...moduleConfig.form,
        create: customCreate as (data: Partial<ExpenseSubCategory>) => Promise<ExpenseSubCategory>,
        update: customUpdate as (id: number, data: Partial<ExpenseSubCategory>) => Promise<ExpenseSubCategory>,
      },
    };
  }, [expenseCategories]);

  return (
    <GenericFormPage<ExpenseSubCategory>
      useData={useExpenseSubCategory}
      config={configWithCategoryOptions}
    />
  );
};

export default Form;

